/**
 * Helpers purs pour le changement de niveau scolaire par l'élève (migration 0151).
 *
 * L'élève peut changer son niveau au plus une fois par mois. L'enforcement réel
 * vit dans le trigger `trg_student_level_cooldown` côté Postgres ; ces helpers ne
 * servent QU'À l'UX (gâter le contrôle, afficher la date de prochaine modification).
 * Ils miroitent l'intervalle SQL `+ interval '1 month'` (mois calendaire, clampé).
 */

/**
 * Ajoute un mois calendaire à une date, en clampant le débordement de jour pour
 * coller à la sémantique Postgres `+ interval '1 month'` (ex. 31 janv. → 28/29 févr.).
 */
export function addOneMonth(d: Date): Date {
  const result = new Date(d);
  const day = result.getDate();
  result.setMonth(result.getMonth() + 1);
  // Débordement (le jour a « sauté » au mois suivant) ⇒ on recule au dernier
  // jour du mois cible, comme le fait Postgres.
  if (result.getDate() < day) result.setDate(0);
  return result;
}

export interface LevelChangeStatus {
  /** Vrai si l'élève peut changer son niveau maintenant. */
  allowed: boolean;
  /**
   * Date à partir de laquelle un nouveau changement sera possible.
   * `null` quand le niveau n'a jamais été changé (changement libre immédiat).
   */
  nextChangeAt: Date | null;
}

/**
 * Détermine si un changement de niveau est autorisé, à partir de l'horodatage du
 * dernier changement (`level_changed_at`, ISO ou `null`).
 */
export function levelChangeStatus(
  levelChangedAt: string | null | undefined,
  now: Date,
): LevelChangeStatus {
  if (!levelChangedAt) return { allowed: true, nextChangeAt: null };
  const nextChangeAt = addOneMonth(new Date(levelChangedAt));
  return { allowed: now.getTime() >= nextChangeAt.getTime(), nextChangeAt };
}

/** Formate la date de prochaine modification pour l'affichage (ex. « 26 juillet 2026 »). */
export function formatLevelChangeDate(d: Date): string {
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}
